import { Injectable } from '@angular/core';
import { Observable ,  BehaviorSubject ,  from as fromPromise ,  of } from 'rxjs';
// 'import {
//   FinanceApiGwService,
//   isObjectContainer,
//   ObjectContainerElement,
// } from '@vwd/finance-api-gw-angular';'
import { InstrumentPillSearchQuery, InstrumentPillSearchResult, InstrumentPillSecurity, Security } from './instrument-pill.model';
// 'import { Security } from '@vwd/ngx-finance-api-extensions';
// import { MatomoService } from '../../../services/utils/analytics/matomo/matomo.service';
import { first } from 'rxjs/operators';
import { AppService } from '../../../../app/app.service';
import { LocalDataService } from '../../utils/ngx-utils/storage';
// import { LogService, Logger } from '@vwd/ngx-logging';

@Injectable()
export class InstrumentPillService {

  private financeApi: any;
  lastUsedSecurities = new Map<string, BehaviorSubject<Security[]>>();
  // private logger: Logger;

  constructor(
    private  appService: AppService,
    private localDataService: LocalDataService,
    // private piwikService: MatomoService,
    // logService: LogService
  ) {
    this.financeApi = this.appService.getInstance();
    // this.logger = logService.openLogger('financeApi/find');
  }

  getSearchResult(search: InstrumentPillSearchQuery): Observable<InstrumentPillSearchResult> {
    return fromPromise(this._getSearchResult(search));
  }

  async _getSearchResult(search: InstrumentPillSearchQuery): Promise<InstrumentPillSearchResult> {

    const result = await this.financeApi.find({
      service: search.serviceName,
      source: 'SW',
      param: {
        searchString: search.query,
        notExchangeId: [1004, 1006, 1034], // pseudo exchanges in backend SW
        limit: 10,
        securityGroupOrder: 1,
        searchArbitrageLogic: 0,
        searchExactInstrumentKey: 1,
        searchExactArbitrage: 1,
        searchSubstring: 1,
        searchFuzzy: 1,
        ...search.params
      }
    }, {
      fieldList: {
        'default.securityObject': [
          'instrumentId',
          'instrumentKey',
          'arbitrageId',
          'description',
          'descriptionShort',
          'securityCategoryCode',
          'entryCountWithoutPerm',
          'wpk',
          'isin',
          'securityCategoryName'
        ]
      }
    });

    if (result.length === 0) {
      const e: any = new Error(`No instruments for '${search.query}' found`);
      e.code = 'INSTRUMENT_NOT_FOUND';
      return Promise.reject(e);
    }


    if (result) {

      const securities = result.map(row => {
        const mySecurity: {default: { securityObject: InstrumentPillSecurity}} = {
          default: {
            securityObject: <InstrumentPillSecurity>{},
          }
        };

        row.forEach((element: any) => {
          const [namespace, objectName] = element.objectName.split('.');
          if (mySecurity[namespace] && mySecurity[namespace][objectName]) {
            mySecurity[namespace][objectName] = element.data;
          }
        });

        return mySecurity;
      });

      return {
        securities: securities.map(o => {
          return {
            instrumentId: this.ensureNumber(o.default.securityObject.instrumentId),
            instrumentKey: o.default.securityObject.instrumentKey,
            arbitrageId: this.ensureNumber(o.default.securityObject.arbitrageId),
            description: o.default.securityObject.description,
            descriptionShort: o.default.securityObject.descriptionShort,
            securityCategoryCode: o.default.securityObject.securityCategoryCode,
            entryCountWithoutPerm: o.default.securityObject.entryCountWithoutPerm,
            wpk: o.default.securityObject.wpk,
            isin: o.default.securityObject.isin,
            securityCategoryName: o.default.securityObject.securityCategoryName
          };
        })
      };
    } else {
      let data: InstrumentPillSecurity[] = [];

      if ((<any>result).data instanceof Array) {
        data = (<any>result).data;
      } else if ((<any>result).data.security instanceof Array) {
        data = (<any>result).data.security; // for service news.searchDropDown
      }

      return {
        securities: data.map((item) => {
          return {
            instrumentId: this.ensureNumber(item.instrumentId),
            instrumentKey: item.instrumentKey,
            arbitrageId: this.ensureNumber(item.arbitrageId),
            descriptionShort: item.descriptionShort || item.description,
            description: item.description,
            securityCategoryCode: item.securityCategoryCode,
            wpk: item.wpk,
            isin: item.isin,
            securityCategoryName: item.securityCategoryName
          };
        })
      };
    }
  }

  loadLastUsedSecurities(scope: string): Observable<Security[]> {
    return of(this.localDataService.get(`lastUsedSecurities:${scope}`) || []);
  }

  saveLastUsedSecurities(lastUsedSecurities: Security[], scope): Observable<boolean> {
    this.localDataService.set(`lastUsedSecurities:${scope}`, lastUsedSecurities.map(this.fixupSecurity));
    return of(true);
  }

  addToLastUsedSecurities(security: Security, scope = 'DEFAULT') {
    this.getLastUsedSecurities(scope).pipe(first()).subscribe(lastUsedSecurities => {
      lastUsedSecurities = lastUsedSecurities.map(this.fixupSecurity).filter(s => s.arbitrageId !== security.arbitrageId);
      lastUsedSecurities.unshift(security);
      lastUsedSecurities = lastUsedSecurities.slice(0, 10);

      this.saveLastUsedSecurities(lastUsedSecurities, scope);
      this.lastUsedSecurities.get(scope).next(lastUsedSecurities);
    });
  }

  getLastUsedSecurities(scope = 'DEFAULT'): Observable<Security[]> {
    if (!this.lastUsedSecurities.get(scope)) {
      const subject = new BehaviorSubject<Security[]>([]);
      this.lastUsedSecurities.set(scope, subject);
      this.loadLastUsedSecurities(scope).subscribe(lastUsedSecurities => {
        subject.next(lastUsedSecurities.map(this.fixupSecurity));
      });
    }

    return this.lastUsedSecurities.get(scope).asObservable();
  }

  findSecurity(param: Partial<InstrumentPillSecurity>): Observable<InstrumentPillSecurity> {
    return fromPromise(this._findSecurity(param));
  }

  async _findSecurity(param: Partial<InstrumentPillSecurity>): Promise<InstrumentPillSecurity> {
    const result = await this.financeApi.find({
      service: 'security.search',
      source: 'SW',
      param: {
        notExchangeId: [1004, 1006, 1034], // pseudo exchanges in backend SW
        limit: 1,
        ...param
      },
      fieldList: {
        'default.securityObject': [
          'instrumentId',
          'instrumentKey',
          'arbitrageId',
          'description',
          'descriptionShort',
          'securityCategoryCode',
          'wpk',
          'isin',
          'securityCategoryName'
        ]
      }
    });

    if (result.length === 0) {
      const e: any = new Error('No instruments found for: ' + JSON.stringify(param));
      e.code = 'INSTRUMENT_NOT_FOUND';
      return Promise.reject(e);
    }

    const objects = result.map(row => {
      const mySecurity: {default: { securityObject: InstrumentPillSecurity}} = {
        default: {
          securityObject: <InstrumentPillSecurity>{},
        }
      };

      row.forEach((element: any) => {
        const [namespace, objectName] = element.objectName.split('.');
        if (mySecurity[namespace] && mySecurity[namespace][objectName]) {
          mySecurity[namespace][objectName] = element.data;
        }
      });

      return mySecurity;
    });

    const securityObject = objects[0].default.securityObject;

    return {
      instrumentId: this.ensureNumber(securityObject.instrumentId),
      instrumentKey: securityObject.instrumentKey,
      arbitrageId: this.ensureNumber(securityObject.arbitrageId),
      description: securityObject.description,
      descriptionShort: securityObject.descriptionShort,
      securityCategoryCode: securityObject.securityCategoryCode,
      wpk: securityObject.wpk,
      isin: securityObject.isin,
      securityCategoryName: securityObject.securityCategoryName
    };
  }

  private ensureNumber(value: string | number): number {
    const val = +value;

    if (isNaN(val)) {
      // this.logger.error('Expected value was not a number:', value);
      return <any>value; // mmhh...
    }

    return val;
  }

  private fixupSecurity(security: InstrumentPillSecurity): InstrumentPillSecurity {
    if (security) {
      if (typeof security.arbitrageId === 'string') {
        security.arbitrageId = this.ensureNumber(security.arbitrageId);
      }
      if (typeof security.instrumentId === 'string') {
        security.instrumentId = this.ensureNumber(security.instrumentId);
      }
    }
    return security;
  }
}
